/**
 * Timer Resume Service
 * Handles the complex logic of resuming time tracking
 * Extracted from resumeTimeTracking function for better maintainability
 */

import moment from "moment-timezone";
import axios from "axios";
import {
  secureSetItem,
  secureGetItem,
  secureRemoveItem,
  clearTimerStorage,
  STORAGE_KEYS,
} from "./secureStorage.js";
import {
  sanitizeTaskName,
  sanitizeProjectName,
  sanitizeTagName,
  sanitizeClientName,
  sanitizeSprintName,
} from "./inputSanitization.js";

/**
 * Stop the current running timer
 * @param {Object} params - Parameters for stopping timer
 * @returns {Promise<Object>} Response data
 */
export const stopCurrentTimer = async ({
  token,
  selectedUser,
  taskName,
  projectName,
  projectId,
  tagName,
  tagId,
  client,
  clientId,
  sprintName,
  sprintId,
  isBillable,
  runningTask,
}) => {
  const localTime = moment().tz(global.config.getTimeZone()).format("YYYY-MM-DD HH:mm:ss");

  const postData = {
    id: runningTask,
    endTime: localTime,
    user: selectedUser,
    task: taskName,
    projectName: projectName,
    project: projectId,
    tagName: tagName,
    tag: tagId,
    client: client,
    clientId: clientId,
    sprintName: sprintName,
    billable: isBillable,
    tz: global.config.getTimeZone(),
    date: localTime,
  };

  if (sprintId) {
    postData.sprint = sprintId;
  }

  const response = await axios.post("/api/time-track/stop-timer", postData, {
    headers: { Authorization: `Bearer ${token}` },
  });

  return response.data;
};

/**
 * Sync timer state to localStorage and Redux with input sanitization
 * @param {Object} params - Timer state parameters
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} actions - Redux actions object
 */
export const syncTimerStateToStorage = (
  {
    task,
    projectName,
    projectId,
    tagName,
    tagId,
    client,
    clientId,
    sprintName,
    sprintId,
    isBillable,
    startTime,
    isRunning,
    runningTaskId,
  },
  dispatch,
  actions
) => {
  // Sanitize all inputs before storage
  const sanitizedTask = task ? sanitizeTaskName(task) : null;
  const sanitizedProjectName = projectName ? sanitizeProjectName(projectName) : null;
  const sanitizedTagName = tagName ? sanitizeTagName(tagName) : null;
  const sanitizedClient = client ? sanitizeClientName(client) : null;
  const sanitizedSprintName = sprintName ? sanitizeSprintName(sprintName) : null;

  // Update localStorage using secure storage
  if (startTime) {
    secureSetItem(STORAGE_KEYS.START_TIME, startTime);
    dispatch(actions.setTimeData(startTime));
  }

  if (isRunning !== undefined) {
    secureSetItem(STORAGE_KEYS.IS_RUNNING, isRunning);
    dispatch(actions.setIsRunningData(isRunning));
  }

  if (sanitizedTask) {
    secureSetItem(STORAGE_KEYS.TASK, sanitizedTask);
    dispatch(actions.setTaskData(sanitizedTask));
  }

  if (sanitizedProjectName) {
    secureSetItem(STORAGE_KEYS.PROJECT, sanitizedProjectName);
    dispatch(actions.setProjectData(sanitizedProjectName));
  }

  if (projectId) {
    secureSetItem(STORAGE_KEYS.PROJECT_ID, projectId);
    dispatch(actions.setProjectIdData(projectId));
  }

  if (sanitizedTagName) {
    secureSetItem(STORAGE_KEYS.TAG, sanitizedTagName);
    dispatch(actions.setTagData(sanitizedTagName));
  }

  if (tagId) {
    secureSetItem(STORAGE_KEYS.TAG_ID, tagId);
    dispatch(actions.setTagIdData(tagId));
  }

  if (sanitizedClient) {
    secureSetItem(STORAGE_KEYS.CLIENT, sanitizedClient);
    dispatch(actions.setClientData(sanitizedClient));
  }

  if (clientId) {
    secureSetItem(STORAGE_KEYS.CLIENT_ID, clientId);
    dispatch(actions.setClientIdData(clientId));
  }

  if (isBillable !== undefined) {
    secureSetItem(STORAGE_KEYS.IS_BILLABLE, isBillable);
    dispatch(actions.setIsbillableData(isBillable));
  }

  if (sanitizedSprintName && sprintId) {
    secureSetItem(STORAGE_KEYS.SPRINT_NAME, sanitizedSprintName);
    dispatch(actions.setSprintNameData(sanitizedSprintName));
    secureSetItem(STORAGE_KEYS.SPRINT_ID, sprintId);
    dispatch(actions.setSprintIdData(sprintId));
  }

  if (runningTaskId) {
    secureSetItem(STORAGE_KEYS.RUNNING_TASK, runningTaskId);
    dispatch(actions.setRunningTaskData(runningTaskId));
  }
};

/**
 * Clear timer state from localStorage and Redux using secure storage
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} actions - Redux actions object
 */
export const clearTimerState = (dispatch, actions) => {
  // Use secure storage to clear all timer-related keys
  try {
    clearTimerStorage(Object.values(STORAGE_KEYS));
  } catch (e) {
    // Fallback: manually remove items if clearTimerStorage fails
    Object.values(STORAGE_KEYS).forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (err) {
        // Ignore errors
      }
    });
  }

  dispatch(actions.setTimeData(null));
  dispatch(actions.setProjectData(null));
  dispatch(actions.setTaskData(null));
  dispatch(actions.setIsRunningData(false));
  dispatch(actions.setRunningTaskData(null));
  dispatch(actions.setTagData(null));
  dispatch(actions.setTagIdData(null));
  dispatch(actions.setProjectIdData(null));
  dispatch(actions.setSprintNameData(null));
  dispatch(actions.setSprintIdData(null));
  dispatch(actions.setClientData(null));
  dispatch(actions.setClientIdData(null));
  dispatch(actions.setIsbillableData(false));
};

/**
 * Validate timer resume data
 * @param {Object} data - Timer data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateTimerResumeData = (data) => {
  const errors = [];
  const { task, projectName, projectId, client, clientId, sprintName, sprintId } = data;

  if (!task) errors.push("Task");
  if (!projectName || !projectId) errors.push("Project");
  if (!client || !clientId) errors.push("Client");
  if (!sprintName || !sprintId) errors.push("Sprint");

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Build post data for starting a new timer
 * @param {Object} params - Timer parameters
 * @returns {Object} Post data object
 */
export const buildTimerStartPostData = ({
  task,
  projectName,
  projectId,
  tagName,
  tagId,
  client,
  clientId,
  sprintName,
  sprintId,
  billable,
  startTime,
  selectedUser,
}) => {
  const postData = {
    task,
    projectName,
    project: projectId,
    tagName,
    tag: tagId,
    startTime,
    user: selectedUser,
    client,
    clientId,
    billable,
    sprint: null,
  };

  if (sprintId) {
    postData.sprintName = sprintName;
    postData.sprint = sprintId;
  }

  return postData;
};

/**
 * Start a new timer
 * @param {Object} params - Parameters for starting timer
 * @returns {Promise<Object>} Response data with timer ID
 */
export const startNewTimer = async ({
  token,
  task,
  projectName,
  projectId,
  tagName,
  tagId,
  client,
  clientId,
  sprintName,
  sprintId,
  billable,
  selectedUser,
}) => {
  const time = moment().tz(global.config.getTimeZone()).utc().format();

  const postData = buildTimerStartPostData({
    task,
    projectName,
    projectId,
    tagName,
    tagId,
    client,
    clientId,
    sprintName,
    sprintId,
    billable,
    startTime: time,
    selectedUser,
  });

  const response = await axios.post("/api/time-track/start-timer", postData, {
    headers: { Authorization: `Bearer ${token}` },
  });

  return {
    ...response.data,
    startTime: time,
  };
};
